import { useCallback, useState } from 'react'
import type { CreateTemplatePayload, Template, TemplateDetail, UpdateTemplatePayload } from 'types/templates'
import type { MutationResult, QueryResult } from 'utils/api'
import { qs } from 'utils/api'

import { useApiQuery } from '../useApiQuery'
import { useFetchApi } from '../useFetchApi'

export interface TemplatesParams {
  skip?: number
  take?: number
  active?: boolean
  includeInactive?: boolean
  q?: string
}

const unwrapTemplates = (response: { data: Template[] }) => response.data

export function useTemplates(params?: TemplatesParams): QueryResult<Template[]> {
  return useApiQuery(`/api/templates${qs(params)}`, { select: unwrapTemplates })
}

export function useTemplate(id: string | undefined): QueryResult<TemplateDetail> {
  return useApiQuery<TemplateDetail>(id ? `/api/templates/${id}` : null)
}

export function useCreateTemplate(): MutationResult<Template, [CreateTemplatePayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (payload: CreateTemplatePayload): Promise<Template> => {
      setLoading(true)
      try {
        return await fetchApi<Template>('/api/templates', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useUpdateTemplate(): MutationResult<Template, [string, UpdateTemplatePayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string, payload: UpdateTemplatePayload): Promise<Template> => {
      setLoading(true)
      try {
        return await fetchApi<Template>(`/api/templates/${id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useDeleteTemplate(): MutationResult<Template, [string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string): Promise<Template> => {
      setLoading(true)
      try {
        return await fetchApi<Template>(`/api/templates/${id}`, { method: 'DELETE' })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}
